import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "pzc-codes";

export type PzcCode = {
  readonly id: string;
  readonly pzc: number;
  readonly bezeichnung: string;
  readonly kategorie?: string;
  readonly geteilt: boolean;
  readonly geteiltVon?: { readonly id: string; readonly name: string };
  readonly geteiltVonOrganisation?: string;
};

function usePzcCodesSyncModule(): SyncModule<ReadonlyArray<PzcCode>> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // PZC-Vorschläge are a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch
      // anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchPzcCodesNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchPzcCodesNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<PzcCode>> {
  const response = await fetch(`${baseUrl}/api/device/sync/pzc`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.pzcCodes ?? []).map(parsePzcCodeNew);
}

function parsePzcCodeNew(entry: any): PzcCode {
  return {
    id: entry.id,
    pzc: entry.pzc,
    bezeichnung: entry.designation,
    kategorie: entry.category || undefined,
    geteilt: entry.shared ?? false,
    geteiltVon: entry.sharedBy
      ? { id: entry.sharedBy.id, name: entry.sharedBy.name }
      : undefined,
    geteiltVonOrganisation: entry.sharedFrom || undefined,
  };
}

export function usePzcCodesSync() {
  const module = usePzcCodesSyncModule();
  useSyncModuleRegistration(module);
}

export function usePzcCodes(): ReadonlyArray<PzcCode> {
  return useSyncData(syncModuleId) ?? [];
}
